import { Link, LinkProps, Text, TextProps } from '@cmpsr/components';
import React from 'react';
import ReactMarkdown, { ExtraProps, Options } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

/**
 * Our standard markdown component for rendering plain text
 * Uses https://github.com/remarkjs/react-markdown
 *
 * If you need to customize how sub elements are rendered, you may want to create
 * a separate component (e.g. for questions on the risk assessment)
 */
function MarkdownText(props: Exclude<Options, 'components'> & TextProps): React.ReactElement {
  const { children, ...rest } = props;
  const transformedChildren =
    typeof children === 'string'
      ? children
          // Replace escaped newlines with actual newlines
          .replace(/\\n/g, '\n')
          // Replace underscores with <u> tags
          .replace(/__(.*?)__/g, '<u>$1</u>')
      : children;

  // Separate ReactMarkdown options from Text style props
  const { remarkPlugins, allowedElements, unwrapDisallowed, skipHtml, ...textStyleProps } = rest;

  // ReactMarkdown options (excluding remarkPlugins which is set explicitly)
  const markdownOptions = { allowedElements, unwrapDisallowed, skipHtml };

  return (
    <ReactMarkdown
      // Allow additional GitHub flavored markdown features
      remarkPlugins={[remarkGfm]}
      // Allow raw HTML
      rehypePlugins={[rehypeRaw]}
      components={{
        p(props: TextProps & ExtraProps) {
          const { node, ...pRest } = props;
          return <Text {...textStyleProps} {...pRest} />;
        },
        a(props: LinkProps & ExtraProps) {
          const { node, ...aRest } = props;
          return (
            <Link
              target="_blank"
              display="inline"
              textDecoration="underline"
              color="text-link-secondary-default"
              {...aRest}
            />
          );
        },
        br: 'br',
      }}
      {...markdownOptions}
      children={transformedChildren}
    />
  );
}

export default MarkdownText;
